/**
 * @fileoverview Layout constants and date helper for the Task Tracker matrix.
 */

import type { CSSProperties } from 'react'

export const STUDENT_COLUMN_MIN_WIDTH = 200
/** Figma: sub-columns are ~110px (single status icon, AR-912). */
export const SUB_COLUMN_MIN_WIDTH = 100
/** Self / Teacher for the final assessment column (no deliverable). */
export const FINAL_SUBCOLS = 2

/** Own scroll container (AR-895) in viewport units, so it behaves the same on the project tab and the page. */
const TABLE_MAX_HEIGHT = '70vh'

const HEADER_BACKGROUND = 'var(--mantine-color-gray-1, #f1f3f5)'
const STICKY_EDGE_SHADOW = '1px 0 0 var(--mantine-color-gray-3, #dee2e6)'

/** Stacking order: body student cell < header row < header/student corner. */
const Z_STICKY_COLUMN = 1
const Z_STICKY_HEADER = 2
const Z_STICKY_CORNER = 3

export const scrollContainerStyle: CSSProperties = { overflow: 'auto', maxHeight: TABLE_MAX_HEIGHT }

/** `collapse` drops cell borders on sticky cells while pinned; `separate` keeps each cell's own border. */
export const tableStyle: CSSProperties = { borderCollapse: 'separate', borderSpacing: 0 }

export const stickyTheadStyle: CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: Z_STICKY_HEADER,
}

const stickyBase: CSSProperties = {
  position: 'sticky',
  left: 0,
  backgroundColor: 'var(--mantine-color-body, #fff)',
  width: STUDENT_COLUMN_MIN_WIDTH,
  minWidth: STUDENT_COLUMN_MIN_WIDTH,
  boxShadow: STICKY_EDGE_SHADOW,
}

export const stickyHeaderStyle: CSSProperties = {
  ...stickyBase,
  backgroundColor: HEADER_BACKGROUND,
  zIndex: Z_STICKY_CORNER,
}
export const stickyBodyStyle: CSSProperties = { ...stickyBase, zIndex: Z_STICKY_COLUMN }

export const headerCellStyle: CSSProperties = {
  backgroundColor: HEADER_BACKGROUND,
  verticalAlign: 'top',
  textAlign: 'center',
}

export const subHeaderStyle: CSSProperties = {
  minWidth: SUB_COLUMN_MIN_WIDTH,
  fontWeight: 500,
  fontSize: '0.75rem',
  color: 'var(--mantine-color-dimmed, #868e96)',
  whiteSpace: 'normal',
  textAlign: 'center',
  backgroundColor: HEADER_BACKGROUND,
}

/** Legend footer (AR-924): hairline above, same inset as the cells. */
export const legendFooterStyle: CSSProperties = {
  borderTop: '1px solid var(--mantine-color-gray-3, #dee2e6)',
  padding: '12px 16px',
  backgroundColor: 'var(--mantine-color-body, #fff)',
}

export const cellStyle: CSSProperties = {
  verticalAlign: 'middle',
  textAlign: 'center',
}

export function formatDate(dateStr: string, locale = 'pt-BR'): string {
  if (!dateStr) return ''
  const d = new Date(`${dateStr}T12:00:00`)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString(locale, { day: 'numeric', month: 'short' })
}
